import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import FindCorrelations from './pages/Wearables/FindCorrelations';
import Dashboard from './pages/Wearables/Dashboard';
import HeartRate from './pages/Wearables/HeartRate';
import Workouts from './pages/Wearables/Workouts';
import Sleep from './pages/Wearables/Sleep';
// import Activity from './pages/Wearables/Activity'; // Hidden: Whoop does not support activity/steps
import Recovery from './pages/Wearables/Recovery';
import Body from './pages/Wearables/Body';
import Devices from './pages/Wearables/Devices';
import LabResults from './pages/LabResults/LabResults';
import Questionnaires from './pages/Questionnaires/Questionnaires';
import DexaScans from './pages/DexaScans/DexaScans';
import Onboarding from './pages/Onboarding/Onboarding';
import Home from './pages/Home';
import HealthDashboard from './pages/Dashboard/Dashboard';
import Privacy from './pages/Privacy';
import Login from './pages/Login/Login';
import WearablesDropdown from './components/WearablesDropdown';
import { useAuth } from './auth/AuthContext';
import { AppShell, Group, Anchor, Button, Text, Container, Burger, Drawer, Stack, Divider } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

const ONBOARDING_ALLOWED_PATHS = ['/onboarding', '/questionnaires', '/lab-results', '/dexa-scans', '/wearables/devices'];

function RequireAuth({ children }) {
  const { loading, isAuthenticated, user } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user && !user.onboarding_completed && !ONBOARDING_ALLOWED_PATHS.some(p => location.pathname.startsWith(p))) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppContent() {
  const { isAuthenticated, user, logout } = useAuth();
  const [opened, { toggle, close }] = useDisclosure(false);
  const onboardingDone = user?.onboarding_completed;
  const pathname = useLocation().pathname;

  const isActive = (path, exact) =>
    exact ? pathname === path : pathname === path || pathname.startsWith(path + '/');

  const navigationLinks = (
    <>
      <Anchor component={Link} to="/" underline="never" c="dark.7" onClick={close} py="xs" fw={isActive('/', true) ? 700 : undefined}>
        About
      </Anchor>
      {isAuthenticated && onboardingDone && (
        <>
          <Anchor component={Link} to="/dashboard" underline="never" c="dark.7" onClick={close} py="xs" fw={isActive('/dashboard', true) ? 700 : undefined}>
            Dashboard
          </Anchor>
          <WearablesDropdown onClose={close} />
          <Anchor component={Link} to="/lab-results" underline="never" c="dark.7" onClick={close} py="xs" fw={isActive('/lab-results') ? 700 : undefined}>
            Lab Results
          </Anchor>
          <Anchor component={Link} to="/dexa-scans" underline="never" c="dark.7" onClick={close} py="xs" fw={isActive('/dexa-scans') ? 700 : undefined}>
            DEXA Scans
          </Anchor>
          <Anchor component={Link} to="/questionnaires" underline="never" c="dark.7" onClick={close} py="xs" fw={isActive('/questionnaires') ? 700 : undefined}>
            Questionnaire
          </Anchor>
        </>
      )}
      <Anchor component={Link} to="/privacy" underline="never" c="dark.7" onClick={close} py="xs" fw={isActive('/privacy', true) ? 700 : undefined}>
        Privacy
      </Anchor>
    </>
  );

  return (
    <AppShell
        header={{ height: { base: 60, sm: 64 } }}
        padding="md"
        styles={{
          main: {
            backgroundColor: '#ffffff',
            paddingTop: 'clamp(4rem, 8vw, 2rem)',
            minHeight: 'calc(100vh - 60px)',
            '@media (min-width: 768px)': {
              paddingTop: 'clamp(2rem, 4vw, 2rem)',
              minHeight: 'calc(100vh - 64px)',
            },
          },
        }}
      >
        <AppShell.Header
          styles={{
            root: {
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #f1f5f9',
            },
          }}
        >
          <Container size="100%" h="100%">
            <Group h="100%" justify="space-between" wrap="nowrap">
              <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                <Anchor component={Link} to="/" underline="never" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Text fw={700} size="h1" letterSpacing={-0.3} c="pink.7" style={{ cursor: 'pointer', fontSize: 'clamp(1rem, 4vw, 1.5rem)' }}>
                    Open Health
                  </Text>
                </Anchor>
              </Group>

              {/* Desktop Navigation */}
              <Group gap="md" visibleFrom="sm" style={{ flexShrink: 0 }}>
                {navigationLinks}
                {isAuthenticated ? (
                  <Button variant="light" color="pink" onClick={logout} size="sm">
                    Log out
                  </Button>
                ) : (
                  <Button color="pink" component={Link} to="/login" size="sm">
                    Log in
                  </Button>
                )}
              </Group>

              {/* Mobile Burger Menu */}
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            </Group>
          </Container>
        </AppShell.Header>

        {/* Mobile Drawer */}
        <Drawer
          opened={opened}
          onClose={close}
          title="Menu"
          padding="md"
          size="xs"
          position="right"
          hiddenFrom="sm"
        >
          <Stack gap="md">
            <Anchor component={Link} to="/" underline="never" c="dark.7" onClick={close} py="xs" fw={pathname === '/' ? 700 : undefined}>
              About
            </Anchor>
            {isAuthenticated && onboardingDone && (
              <>
                <Anchor component={Link} to="/dashboard" underline="never" c="dark.7" onClick={close} py="xs" fw={pathname === '/dashboard' ? 700 : undefined}>
                  Dashboard
                </Anchor>
                <Divider label="Wearables" labelPosition="left" />
                <Anchor component={Link} to="/wearables/dashboard" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/dashboard' ? 700 : undefined}>
                  Dashboard
                </Anchor>
                <Anchor component={Link} to="/wearables/find-correlations" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/find-correlations' ? 700 : undefined}>
                  Find Correlations
                </Anchor>
                <Anchor component={Link} to="/wearables/heart-rate" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/heart-rate' ? 700 : undefined}>
                  Heart Rate
                </Anchor>
                <Anchor component={Link} to="/wearables/workouts" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/workouts' ? 700 : undefined}>
                  Workouts
                </Anchor>
                <Anchor component={Link} to="/wearables/sleep" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/sleep' ? 700 : undefined}>
                  Sleep
                </Anchor>
                {/* <Anchor component={Link} to="/wearables/activity" underline="never" c="dark.7" onClick={close} py="xs" pl="md">
                  Activity
                </Anchor> */}
                <Anchor component={Link} to="/wearables/recovery" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/recovery' ? 700 : undefined}>
                  Recovery
                </Anchor>
                <Anchor component={Link} to="/wearables/body" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/body' ? 700 : undefined}>
                  Body
                </Anchor>
                <Anchor component={Link} to="/wearables/devices" underline="never" c="dark.7" onClick={close} py="xs" pl="md" fw={pathname === '/wearables/devices' ? 700 : undefined}>
                  Devices
                </Anchor>
                <Divider />
                <Anchor component={Link} to="/lab-results" underline="never" c="dark.7" onClick={close} py="xs" fw={pathname.startsWith('/lab-results') ? 700 : undefined}>
                  Lab Results
                </Anchor>
                <Anchor component={Link} to="/dexa-scans" underline="never" c="dark.7" onClick={close} py="xs" fw={pathname.startsWith('/dexa-scans') ? 700 : undefined}>
                  DEXA Scans
                </Anchor>
                <Anchor component={Link} to="/questionnaires" underline="never" c="dark.7" onClick={close} py="xs" fw={pathname.startsWith('/questionnaires') ? 700 : undefined}>
                  Questionnaire
                </Anchor>
              </>
            )}
            <Anchor component={Link} to="/privacy" underline="never" c="dark.7" onClick={close} py="xs" fw={pathname === '/privacy' ? 700 : undefined}>
              Privacy
            </Anchor>
            <Divider />
            {isAuthenticated ? (
              <Button variant="light" color="pink" onClick={() => { logout(); close(); }} fullWidth>
                Log out
              </Button>
            ) : (
              <Button color="pink" component={Link} to="/login" onClick={close} fullWidth>
                Log in
              </Button>
            )}
          </Stack>
        </Drawer>

        <AppShell.Main style={{ paddingLeft: 'clamp(1rem, 4vw, 4rem)', paddingRight: 'clamp(1rem, 4vw, 4rem)' }}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <Onboarding />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <HealthDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables"
              element={
                <RequireAuth>
                  <Navigate to="/wearables/dashboard" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/find-correlations"
              element={
                <RequireAuth>
                  <FindCorrelations />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/heart-rate"
              element={
                <RequireAuth>
                  <HeartRate />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/workouts"
              element={
                <RequireAuth>
                  <Workouts />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/sleep"
              element={
                <RequireAuth>
                  <Sleep />
                </RequireAuth>
              }
            />
            {/* <Route
              path="/wearables/activity"
              element={
                <RequireAuth>
                  <Activity />
                </RequireAuth>
              }
            /> */}
            <Route
              path="/wearables/recovery"
              element={
                <RequireAuth>
                  <Recovery />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/body"
              element={
                <RequireAuth>
                  <Body />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/devices"
              element={
                <RequireAuth>
                  <Devices />
                </RequireAuth>
              }
            />
            <Route
              path="/wearables/settings"
              element={<Navigate to="/wearables/devices" replace />}
            />
            <Route
              path="/lab-results"
              element={
                <RequireAuth>
                  <LabResults />
                </RequireAuth>
              }
            />
            <Route
              path="/dexa-scans"
              element={
                <RequireAuth>
                  <DexaScans />
                </RequireAuth>
              }
            />
            <Route
              path="/questionnaires"
              element={
                <RequireAuth>
                  <Questionnaires />
                </RequireAuth>
              }
            />
            <Route path="/privacy" element={<Privacy />} />
          </Routes>
        </AppShell.Main>
      </AppShell>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;

